import {
    DuplicateEmailError,
    AuthenticationError,
    ApiError,
} from '../types/errors';

// In development, suppress Next.js error overlay for expected errors
export function suppressExpectedErrors() {
    if (
        process.env.NODE_ENV === 'development' &&
        typeof window !== 'undefined'
    ) {
        // Override window.onerror to suppress expected errors
        const originalOnError = window.onerror;
        window.onerror = function (message, source, lineno, colno, error) {
            // Check if this is a MetaMask/Web3 error
            if (
                typeof message === 'string' &&
                (message.includes('MetaMask') ||
                    message.includes('eth_accounts') ||
                    message.includes('eth_requestAccounts') ||
                    message.includes('Failed to connect to MetaMask') ||
                    message.includes('The method does not exist'))
            ) {
                console.log(
                    '[PAIR-APP] Suppressing MetaMask/Web3 error:',
                    message
                );
                return true; // Suppress the error
            }

            // Check if this is a browser extension background script error
            if (
                typeof message === 'string' &&
                (message.includes('background/carbon') ||
                    message.includes('background/framework') ||
                    message.includes('background-carbon') ||
                    message.includes('background-framework') ||
                    message.includes('background-legacy') ||
                    message.includes('getUserLoginStatus') ||
                    message.includes('getGlobalExtensionSettings') ||
                    message.includes('CarbonApi'))
            ) {
                // Silently suppress - these are from browser extensions, not our code
                return true; // Suppress the error without logging
            }

            // Check if this is a PostHog error
            if (
                typeof message === 'string' &&
                (message.includes('posthog') ||
                    message.includes('PostHog') ||
                    message.includes('Failed to fetch') ||
                    message.includes('ERR_BLOCKED_BY_CLIENT'))
            ) {
                // Don't log to avoid flooding - just suppress
                return true; // Suppress the error
            }

            // Check if this is an expected error
            if (
                error instanceof DuplicateEmailError ||
                error instanceof AuthenticationError ||
                (error instanceof ApiError &&
                    error.errorCode === 'DUPLICATE_EMAIL') ||
                (typeof message === 'string' &&
                    (message.includes('already exists') ||
                        message.includes('email already exists') ||
                        message.includes('Could not sign up user')))
            ) {
                console.log(
                    '[PAIR-APP] Suppressing expected error from Next.js overlay:',
                    message
                );
                return true; // Suppress the error
            }

            // Call original handler for unexpected errors
            if (originalOnError) {
                return originalOnError.call(
                    window,
                    message,
                    source,
                    lineno,
                    colno,
                    error
                );
            }
            return false;
        };

        // Also override unhandledrejection
        window.addEventListener(
            'unhandledrejection',
            (event) => {
                // Check if this is a MetaMask/Web3 error
                if (
                    event.reason &&
                    (event.reason.message?.includes('MetaMask') ||
                        event.reason.message?.includes('eth_accounts') ||
                        event.reason.message?.includes('eth_requestAccounts') ||
                        event.reason.message?.includes(
                            'Failed to connect to MetaMask'
                        ) ||
                        event.reason.message?.includes(
                            'The method does not exist'
                        ) ||
                        event.reason.code === -32601 || // JSON-RPC error code for method not found
                        event.reason.code === 4001) // User rejected request
                ) {
                    console.log(
                        '[PAIR-APP] Suppressing MetaMask/Web3 rejection:',
                        event.reason
                    );
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                // Check if this is a browser extension background script error
                if (
                    event.reason &&
                    event.reason.message &&
                    (event.reason.message.includes('background/carbon') ||
                        event.reason.message.includes('background/framework') ||
                        event.reason.message.includes('background-carbon') ||
                        event.reason.message.includes('background-framework') ||
                        event.reason.message.includes('background-legacy') ||
                        event.reason.message.includes('getUserLoginStatus') ||
                        event.reason.message.includes(
                            'getGlobalExtensionSettings'
                        ) ||
                        event.reason.message.includes('CarbonApi'))
                ) {
                    // Silently suppress - these are from browser extensions
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                // Check if this is a PostHog error
                if (
                    event.reason &&
                    (event.reason.message?.includes('posthog') ||
                        event.reason.message?.includes('PostHog') ||
                        event.reason.message?.includes('Failed to fetch') ||
                        event.reason.message?.includes(
                            'ERR_BLOCKED_BY_CLIENT'
                        ) ||
                        event.reason.message?.includes('/decide') ||
                        event.reason.message?.includes('/e?ip=1'))
                ) {
                    // Don't log to avoid flooding - just suppress
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                if (
                    event.reason instanceof DuplicateEmailError ||
                    event.reason instanceof AuthenticationError ||
                    (event.reason instanceof ApiError &&
                        event.reason.errorCode === 'DUPLICATE_EMAIL') ||
                    (event.reason instanceof Error &&
                        (event.reason.message.includes('already exists') ||
                            event.reason.message.includes(
                                'email already exists'
                            ) ||
                            event.reason.message.includes(
                                'Could not sign up user'
                            )))
                ) {
                    console.log(
                        '[PAIR-APP] Suppressing expected rejection from Next.js overlay:',
                        event.reason
                    );
                    event.preventDefault();
                    event.stopPropagation();
                }
            },
            true
        ); // Use capture phase to catch it early
    }
}

// Helper to wrap async operations and suppress expected errors
export async function withExpectedErrorHandling<T>(
    operation: () => Promise<T>,
    onExpectedError?: (error: any) => void
): Promise<T | null> {
    try {
        return await operation();
    } catch (error) {
        if (
            error instanceof DuplicateEmailError ||
            error instanceof AuthenticationError ||
            (error instanceof ApiError && error.errorCode === 'DUPLICATE_EMAIL')
        ) {
            console.log(
                '[PAIR-APP] Expected error caught and suppressed:',
                error
            );
            if (onExpectedError) {
                onExpectedError(error);
            }
            return null;
        }
        throw error;
    }
}
